if (!customElements.get('geolocation-recommendation')) {
  class GeolocationRecommendation extends HTMLElement {
    constructor() {
      super();
      this.STORAGE_KEY = 'tattup_geo_handled';
      this.panel = this.querySelector('.geo-popup__panel');
      this.overlay = this.querySelector('.geo-popup__overlay');
      this.form = this.querySelector('form.localization');
      this.headingEl = this.querySelector('.geo-popup__heading');
      this.textEl = this.querySelector('.geo-popup__text');
      this.flagEl = this.querySelector('.geo-popup__flag');
      this.dismissBtn = this.querySelector('.js-geo-dismiss');
      this.closeBtn = this.querySelector('.js-geo-close');
      this.countryInput = this.querySelector('input[name="country_code"]');
      this.langInput = this.querySelector('input[name="locale_code"]');

      this.currentCountry = this.dataset.currentCountry || '';
      this.currentLanguage = this.dataset.currentLanguage || '';
      this.countries = this.safeParse(this.dataset.countries) || {};
      this.languages = this.safeParse(this.dataset.languages) || [];
      this.detected = null;

      this.headingTemplate = this.dataset.title || (this.headingEl ? this.headingEl.textContent : '');
      this.textTemplate = this.dataset.text || (this.textEl ? this.textEl.textContent : '');
      // Copy for the "we don't ship to <country>" (non-market) case, rendered into data-* by the section.
      this.unavailableTitleTemplate = this.dataset.unavailableTitle || this.headingTemplate;
      this.unavailableTextTemplate = this.dataset.unavailableText || this.textTemplate;
      this.unavailableDismissText = this.dataset.unavailableDismiss || '';
      this.dismissTemplate = this.dismissBtn ? this.dismissBtn.textContent : '';

      this.onOpenRequest = this.onOpenRequest.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onManualChange = this.onManualChange.bind(this);
    }

    connectedCallback() {
      document.addEventListener('geo:open', this.onOpenRequest);
      document.addEventListener('change', this.onManualChange, true);
      // close (X), overlay and Escape are a dismissal too: remembered like «Bei Schweiz bleiben»
      if (this.dismissBtn) this.dismissBtn.addEventListener('click', () => this.dismiss());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.dismiss());
      if (this.overlay) this.overlay.addEventListener('click', () => this.dismiss());
      if (this.form) this.form.addEventListener('submit', () => this.persist((this.countryInput && this.countryInput.value) || (this.detected && this.detected.handle)));
      this.maybeAutoOpen();
    }

    disconnectedCallback() {
      document.removeEventListener('geo:open', this.onOpenRequest);
      document.removeEventListener('change', this.onManualChange, true);
      document.removeEventListener('keydown', this.onKeydown);
    }

    safeParse(s) { try { return JSON.parse(s); } catch (e) { return null; } }

    // ISO-3166 alpha-2 -> regional-indicator flag emoji (e.g. 'FR' -> 🇫🇷).
    // Renders as a flag on iOS/Android/macOS; degrades to the 2 letters on Windows.
    isoToFlag(iso) {
      if (!iso || !/^[a-zA-Z]{2}$/.test(iso)) return '';
      return iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
    }

    getDebugCountry() {
      try { return new URLSearchParams(window.location.search).get('geo_debug'); } catch (e) { return null; }
    }

    // The answer is kept in localStorage AND in a cookie on .tattup.shop: a country change can move the visitor
    // to another shop domain (eu. / international.tattup.shop), where this origin's localStorage is not visible.
    getDismissed() {
      let v = null;
      try { v = window.localStorage.getItem(this.STORAGE_KEY); } catch (e) { /* private mode */ }
      if (v) return v;
      const m = document.cookie.match(new RegExp('(?:^|; )' + this.STORAGE_KEY + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    }

    persist(country) {
      const v = String(country || 'handled');
      try { window.localStorage.setItem(this.STORAGE_KEY, v); } catch (e) { /* private mode */ }
      const host = window.location.hostname;
      const domain = /(^|\.)tattup\.shop$/.test(host) ? '; domain=.tattup.shop' : '';
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = this.STORAGE_KEY + '=' + encodeURIComponent(v) + '; path=/; max-age=31536000; SameSite=Lax' + domain + secure;
    }

    // A manual country change anywhere (footer / announcement-bar selector, or the popup's own selector) counts
    // as an answer. The selectors submit with form.submit() (no submit event), so listen to their change event;
    // capture phase, so it runs before their inline handler starts the navigation.
    onManualChange(e) {
      const form = e.target && e.target.closest && e.target.closest('form.localization');
      if (!form) return;
      const chosen = (e.detail && e.detail.selectedValue) || (e.target && e.target.value) || 'manual';
      this.persist(chosen);
    }

    async fetchDetected() {
      try {
        const res = await fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const data = await res.json();
        const c = data && data.detected_values && data.detected_values.country;
        if (!c || !c.handle) return null;
        const fallback = this.countries[c.handle] && this.countries[c.handle].name;
        return { handle: c.handle, name: c.name || fallback || c.handle };
      } catch (e) { return null; }
    }

    async maybeAutoOpen() {
      if (!window.GeoDetect) return;
      const debug = this.getDebugCountry();
      if (debug) {
        this.detected = { handle: debug, name: (this.countries[debug] && this.countries[debug].name) || debug };
      } else {
        this.detected = await this.fetchDetected();
      }
      const detectedCountry = this.detected ? this.detected.handle : null;
      const suggestedLanguage = window.GeoDetect.deriveSuggestedLanguage(
        navigator.language, this.currentLanguage, this.languages
      );
      const decision = window.GeoDetect.decideGeoSuggestion({
        debugCountry: debug || null,
        dismissedCountry: this.getDismissed(),
        detectedCountry: detectedCountry,
        currentCountry: this.currentCountry,
        availableCountries: Object.keys(this.countries),
        suggestedLanguage: suggestedLanguage
      });
      // 'market'      -> suggest switching to the detected market ("Shopping from <country>?").
      // 'unavailable' -> visitor is in a country we don't ship to (e.g. Tunisia): show the
      //                  "we don't ship to <country>" popup so they can pick a region we serve.
      //                  decision.country is the detected country for DISPLAY ONLY here.
      // 'language'/null -> nothing auto-opens (language-only has no dedicated UI yet).
      if (decision.mode === 'market') {
        this.prefill(decision.country, decision.language);
        this.show();
      } else if (decision.mode === 'unavailable') {
        this.prefillUnavailable(decision.country, decision.language);
        this.show();
      }
    }

    prefill(countryIso, languageIso) {
      const info = this.countries[countryIso] || {};
      const name = (this.detected && this.detected.handle === countryIso && this.detected.name) || info.name || countryIso;
      const currency = info.currency || '';
      if (this.flagEl) this.flagEl.textContent = this.isoToFlag(countryIso);
      if (this.headingEl) this.headingEl.textContent = this.headingTemplate.replace('__COUNTRY__', name);
      if (this.textEl) this.textEl.textContent = this.textTemplate.replace('__CURRENCY__', currency);
      if (this.countryInput) this.countryInput.value = countryIso;
      const csBtn = this.querySelector('.localization__selector .custom-select__btn');
      if (csBtn && csBtn.firstElementChild) csBtn.firstElementChild.textContent = name;
      if (this.dismissBtn && this.dismissTemplate) this.dismissBtn.textContent = this.dismissTemplate;
      if (languageIso && this.langInput) this.langInput.value = languageIso;
    }

    // Non-market visitor (e.g. Tunisia): show the detected country's flag/name in a "we don't
    // ship here" framing, but DELIBERATELY leave country_code at the current valid market — the
    // detected country is not a selectable market, so writing it into the form would break submit.
    // The visitor picks a region we serve from the selector (which lazy-loads on open).
    prefillUnavailable(countryIso, languageIso) {
      const name = (this.detected && this.detected.handle === countryIso && this.detected.name) || countryIso;
      if (this.flagEl) this.flagEl.textContent = this.isoToFlag(countryIso);
      if (this.headingEl) this.headingEl.textContent = this.unavailableTitleTemplate.replace('__COUNTRY__', name);
      if (this.textEl) this.textEl.textContent = this.unavailableTextTemplate.replace('__COUNTRY__', name);
      if (this.dismissBtn && this.unavailableDismissText) this.dismissBtn.textContent = this.unavailableDismissText;
      if (languageIso && this.langInput) this.langInput.value = languageIso;
    }

    show() {
      this.hidden = false;
      this.classList.add('is-open');
      document.documentElement.classList.add('geo-popup-open');
      this.previouslyFocused = document.activeElement;
      document.addEventListener('keydown', this.onKeydown);
      const focusable = this.panel && this.panel.querySelector('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus();
    }

    hide() {
      this.classList.remove('is-open');
      this.hidden = true;
      document.documentElement.classList.remove('geo-popup-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.previouslyFocused && this.previouslyFocused.focus) this.previouslyFocused.focus();
    }

    dismiss() {
      this.persist(this.detected ? this.detected.handle : this.currentCountry);
      this.hide();
    }

    onOpenRequest() {
      // Reopen prefilled with the detected country only if it is an actual market;
      // otherwise the current market (avoids empty currency copy + invalid country_code).
      const handle = this.detected && this.detected.handle;
      const iso = handle && this.countries[handle] ? handle : this.currentCountry;
      this.prefill(iso, null);
      this.show();
    }

    onKeydown(e) {
      if (e.key === 'Escape') { this.dismiss(); return; }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      if (!this.panel) return;
      const focusables = Array.from(
        this.panel.querySelectorAll('button, [href], input, select, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  customElements.define('geolocation-recommendation', GeolocationRecommendation);
}
